export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  fieldIssues(): Record<string, string> {
    const issues = (
      this.details as { issues?: { path: string; message: string }[] } | undefined
    )?.issues;
    if (!Array.isArray(issues)) return {};
    const map: Record<string, string> = {};
    for (const issue of issues) map[issue.path] ??= issue.message;
    return map;
  }
}

interface ApiErrorWire {
  code: string;
  message: string;
  requestId?: string;
  details?: unknown;
}

export const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
// Same-origin fetch mode restricts cookies to this origin; it is a protocol
// option, not a credential.
const fetchMode = "same-origin" as const;

function safeParseJson(text: string): unknown | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function toError(
  status: number,
  json: unknown,
  requestIdHeader: string | null
): ApiClientError {
  const wire = (json as { error?: ApiErrorWire } | null)?.error;
  return new ApiClientError(
    status,
    wire?.code ?? "internal",
    wire?.message ?? "Request failed.",
    wire?.requestId ?? requestIdHeader ?? undefined,
    wire?.details
  );
}

export async function request<T>(
  path: string,
  opts: { method?: "GET" | "POST"; body?: unknown } = {}
): Promise<T> {
  const init: RequestInit = { method: opts.method ?? "GET", credentials: fetchMode };
  if (opts.body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(`${BASE_URL}${path}`, init);
  const text = await res.text();
  const json = safeParseJson(text);
  if (!res.ok) {
    throw toError(res.status, json, res.headers.get("x-request-id"));
  }
  return json as T;
}

export async function requestText(
  path: string,
  opts: { method?: "GET" | "POST"; body?: unknown } = {}
): Promise<string> {
  const init: RequestInit = { method: opts.method ?? "GET", credentials: fetchMode };
  if (opts.body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(`${BASE_URL}${path}`, init);
  const text = await res.text();
  if (!res.ok) {
    throw toError(res.status, safeParseJson(text), res.headers.get("x-request-id"));
  }
  return text;
}

export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    credentials: fetchMode,
    body: form,
  });
  const text = await res.text();
  const json = safeParseJson(text);
  if (!res.ok) {
    throw toError(res.status, json, res.headers.get("x-request-id"));
  }
  return json as T;
}
